import React, { useState, useEffect } from "react";
import { Tooltip, Modal } from "antd";
import api from "../../services/api";
import { notification } from "antd";
import {
  VerificationContainer,
  VerificationTitle,
  VerificationCardContainer,
  VerificationCardContent,
  VerificationCardTitle,
  VerificationCardSubtitle,
  VerificationPhoneNumber,
  VerificationCodeContainer,
  VerificationCodeTitle,
  VerificationCodeInputContainer,
  VerificationCodeInput,
  VerificationResendContainer,
  ResendButton,
  TimerText,
  ButtonCancel,
  InfoText,
  BoxButton,
  ButtonPin,
} from "./styles";

interface VerificationPhoneProps {
  userId: number;
  phoneNumber: string;
  onVerified: () => void;
  onCancel?: () => void;
  title?: string;
  showCancelConfirmation?: boolean;
}

const VerificationPhone: React.FC<VerificationPhoneProps> = ({
  userId,
  phoneNumber,
  onVerified,
  onCancel,
  title = "Verificação de Telefone",
  showCancelConfirmation = true,
}) => {
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState<number>(0);

  const RESEND_INTERVAL = 60;

  const startResendCountdown = () => {
    setResendTimer(RESEND_INTERVAL);
    localStorage.setItem(
      "phoneVerificationExpiry",
      String(Date.now() + RESEND_INTERVAL * 1000)
    );
  };

  useEffect(() => {
    const expiry = localStorage.getItem("phoneVerificationExpiry");
    if (expiry) {
      const remaining = Math.ceil((parseInt(expiry) - Date.now()) / 1000);
      if (remaining > 0) {
        setResendTimer(remaining);
      } else {
        localStorage.removeItem("phoneVerificationExpiry");
      }
    }
  }, []);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          localStorage.removeItem("phoneVerificationExpiry");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const formatPhoneNumber = (phone: string): string => {
    if (!phone || phone.length < 10) return phone;

    return `(${phone.substring(0, 2)}) ${phone.substring(
      2,
      7
    )}-${phone.substring(7)}`;
  };

  const sendVerificationCode = async () => {
    setVerificationLoading(true);

    try {
      const payload = {
        id: userId,
        phone: phoneNumber,
      };

      const response = await api.post("/user/send-pin-user", payload);

      setVerificationSent(true);
      startResendCountdown();
      notification.success({
        message: "Código de verificação enviado",
        description:
          response.data.message ||
          "Um código de verificação foi enviado para seu telefone.",
      });
    } catch (error: any) {
      if (
        error.response.data.message ===
        "Número de telefone já está em uso por outro usuário"
      ) {
        notification.warning({
          message: "Erro ao enviar código",
          description:
            "O número de telefone já está em uso por outro usuário. Por favor, use um número diferente.",
        });
      } else {
        notification.error({
          message: "Erro ao enviar código",
          description:
            "Não foi possível enviar o código de verificação. Tente novamente.",
        });
      }
    } finally {
      setVerificationLoading(false);
    }
  };

  const verifyPhone = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      notification.warning({
        message: "Código inválido",
        description: "Por favor, digite o código de 6 dígitos completo.",
      });
      return;
    }

    setVerificationLoading(true);

    try {
      const payload = {
        id: userId,
        pin_phone: verificationCode,
      };

      const response = await api.post("/user/check-pin-phone", payload);

      notification.success({
        message: response.data.message || "Telefone verificado com sucesso",
        duration: 3,
      });

      onVerified();
    } catch (error) {
      console.error("Erro ao verificar código:", error);
      notification.error({
        message: "Erro na verificação",
        description: "Ocorreu um erro ao verificar o código. Tente novamente.",
      });
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleCancel = () => {
    if (showCancelConfirmation && verificationSent) {
      Modal.confirm({
        title: "Cancelar verificação",
        content:
          "Se você cancelar agora, precisará iniciar o processo novamente. Deseja continuar?",
        okText: "Sim, cancelar",
        cancelText: "Não, continuar",
        onOk() {
          if (onCancel) onCancel();
        },
      });
    } else {
      if (onCancel) onCancel();
    }
  };

  return (
    <VerificationContainer>
      {!verificationSent ? (
        <VerificationCardContainer>
          <VerificationTitle>{title}</VerificationTitle>
          <VerificationCardTitle>
            Precisamos verificar seu número de telefone antes de continuar.
          </VerificationCardTitle>
          <VerificationCardContent>
            <VerificationCardSubtitle>
              Um código de verificação será enviado para o número:
            </VerificationCardSubtitle>
            <VerificationPhoneNumber>
              {formatPhoneNumber(phoneNumber)}
            </VerificationPhoneNumber>
          </VerificationCardContent>
          <BoxButton>
            <Tooltip
              title={
                resendTimer > 0
                  ? `Aguarde ${resendTimer}s para enviar o código`
                  : ""
              }
            >
              <ButtonPin
                type="primary"
                size="large"
                loading={verificationLoading}
                onClick={sendVerificationCode}
                disabled={resendTimer > 0}
              >
                {resendTimer > 0
                  ? `Enviar Código (${resendTimer}s)`
                  : "Enviar Código"}
              </ButtonPin>
            </Tooltip>
            {onCancel && (
              <ButtonCancel onClick={handleCancel}>
                Voltar para edição
              </ButtonCancel>
            )}
          </BoxButton>
        </VerificationCardContainer>
      ) : (
        <VerificationCodeContainer>
          <VerificationCodeTitle>
            Digite o código de verificação enviado para seu telefone:
          </VerificationCodeTitle>
          <VerificationCodeInputContainer>
            <VerificationCodeInput
              placeholder="Código de verificação"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              maxLength={6}
            />
          </VerificationCodeInputContainer>
          <VerificationResendContainer>
            Não recebeu o código?
            <Tooltip
              title={
                resendTimer > 0
                  ? `Aguarde ${resendTimer}s para reenviar o código`
                  : ""
              }
            >
              <ResendButton
                type="link"
                onClick={sendVerificationCode}
                loading={verificationLoading}
                disabled={resendTimer > 0}
              >
                {resendTimer > 0 ? (
                  <>
                    Reenviar <TimerText>({resendTimer}s)</TimerText>
                  </>
                ) : (
                  "Reenviar"
                )}
              </ResendButton>
            </Tooltip>
          </VerificationResendContainer>
          <ButtonPin
            type="primary"
            onClick={verifyPhone}
            loading={verificationLoading}
          >
            Verificar
          </ButtonPin>
          {onCancel && (
            <ButtonCancel onClick={handleCancel}>
              Voltar para edição
            </ButtonCancel>
          )}
          <InfoText>
            Se precisar corrigir o número de telefone, volte para a tela
            anterior.
          </InfoText>
        </VerificationCodeContainer>
      )}
    </VerificationContainer>
  );
};

export default VerificationPhone;
